import React, { useState } from 'react';
import './GroupMembershipTab.css';
import api from '../../wrapper/API';
import Popup from '../../popup/Popup';
import ActionButton from '../../input/ActionButton';
import SegmentedControl from '../../input/SegmentedControl';
import { usePopup } from '../../context/PopupContext';
import { useSuccessBar } from '../../context/SuccessBarContext';
import { VALIDITY_MODE_LABEL } from '../../../util/participantGroups';
import { apiErrorMessage, formatDateDe, memberCountLabel, todayIso } from './groupAdminUtils';

const VALIDITY_OPTIONS = [
  { value: 'CalendarYear', label: VALIDITY_MODE_LABEL.CalendarYear, icon: 'calendar_month', tone: 'accent' },
  { value: 'OpenEnded', label: VALIDITY_MODE_LABEL.OpenEnded, icon: 'all_inclusive', tone: 'accent' },
  { value: 'DateRange', label: VALIDITY_MODE_LABEL.DateRange, icon: 'date_range', tone: 'accent' },
];

const VALIDITY_EXPLANATION = {
  CalendarYear: 'Neue Mitgliedschaften laufen bis zum 31.12. des Jahres. Über „Verlängern“ gilt eine Mitgliedschaft ein weiteres Kalenderjahr.',
  OpenEnded: 'Neue Mitgliedschaften werden ohne Enddatum vorbelegt und gelten, bis sie beendet werden.',
  DateRange: 'Beginn und Ende werden beim Zuweisen frei gewählt.',
};

/** Tab "Mitgliedschaft" (not for guest): validity mode, current member count, "end all memberships". */
function GroupMembershipTab({ form, setField, group, guest, isCreate, isOffline, refreshGroups, showError }) {
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [endDate, setEndDate] = useState(() => todayIso());
  const [ending, setEnding] = useState(false);

  const handleConfirmEnd = () => {
    closePopup();
    if (!group || !endDate) return;
    setEnding(true);
    startSuccessBarLoading('Mitgliedschaften werden beendet');
    api.post(`/ParticipantGroup/${group.id}/EndAllMemberships?date=${encodeURIComponent(endDate)}`)
      .then(async (res) => {
        const ended = Number(res.data?.ended ?? 0);
        await refreshGroups();
        startSuccessBarSuccess(ended === 0
          ? 'Keine Mitgliedschaft betroffen'
          : ended === 1 ? '1 Mitgliedschaft beendet' : `${ended} Mitgliedschaften beendet`);
      })
      .catch(err => {
        closeSuccessBar();
        showError('Beenden fehlgeschlagen', apiErrorMessage(err, 'Die Mitgliedschaften konnten nicht beendet werden.'));
      })
      .finally(() => setEnding(false));
  };

  const endsBeforeToday = endDate && endDate < todayIso();
  const guestName = guest?.name || 'Gast';

  return (
    <div className="groupTabBody">
      <section className="groupEditorSection">
        <h4 className="groupEditorSectionTitle">Gültigkeit neuer Mitgliedschaften</h4>
        <SegmentedControl
          options={VALIDITY_OPTIONS}
          value={form.validityMode}
          onChange={value => setField('validityMode', value)}
          ariaLabel="Gültigkeit neuer Mitgliedschaften"
        />
        <p className="groupEditorExplanation">{VALIDITY_EXPLANATION[form.validityMode]}</p>
        <p className="formFieldHint">
          Der Modus belegt nur das Formular beim Zuweisen vor – bestehende Mitgliedschaften bleiben unverändert.
          Unbefristete Mitgliedschaften (z. B. über eine Rolle) sind in jedem Modus möglich.
        </p>
      </section>

      {!isCreate && group && (
        <React.Fragment>
          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Aktuelle Mitglieder</h4>
            <p className="groupMembershipCount">
              <span className="groupMembershipCountValue">{memberCountLabel(group)}</span>
              <span className="groupMembershipCountCaption">mit heute gültiger Mitgliedschaft</span>
            </p>
            <p className="formFieldHint">
              Einzelne Mitgliedschaften weist du im Profil der Person (Abschnitt „Gruppe“) zu und verlängerst oder
              beendest sie dort. Unter Spieler › Tab „Gruppen“ siehst du die Mitglieder je Jahr.
            </p>
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Alle Mitgliedschaften beenden</h4>
            <div className="groupMembershipEndRow">
              <div className="formFieldWrapper">
                <label className="formFieldLabel" htmlFor="groupEndAllDate">Letzter gültiger Tag</label>
                <input
                  id="groupEndAllDate"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  required
                />
              </div>
              <ActionButton
                icon="event_busy"
                label="Alle beenden …"
                type={1}
                onClick={() => openPopup('confirmendgroupmemberships', group.id)}
                disabled={isOffline || ending || !endDate}
              />
            </div>
            <p className="formFieldHint">
              {isOffline
                ? 'Offline nicht verfügbar.'
                : 'Beendet alle Mitgliedschaften dieser Gruppe zum gewählten Tag; später beginnende werden gelöscht. '
                  + 'Zum Archivieren der Gruppe ein Datum vor heute wählen – es darf keine Mitgliedschaft mehr heute oder später gelten.'}
            </p>
          </section>

          <Popup
            name="confirmendgroupmemberships"
            headerText="Mitgliedschaften beenden"
            confirmText="Beenden"
            type="confirmaction"
            onConfirm={handleConfirmEnd}
            withCancel
          >
            <p>
              Alle Mitgliedschaften der Gruppe <b>{group.name}</b> enden am <b>{formatDateDe(endDate)}</b>.
              Später beginnende Mitgliedschaften werden gelöscht.
            </p>
            <p className="formFieldHint">
              Für Spieltage nach diesem Tag zählen die Personen zu ihrer dann gültigen Gruppe
              (ohne weitere Mitgliedschaft: {guestName}); offene (unbezahlte) Preise werden neu berechnet, bezahlte bleiben.
              {endsBeforeToday ? ' Danach kann die Gruppe archiviert werden.' : ''}
            </p>
          </Popup>
        </React.Fragment>
      )}
    </div>
  );
}

export default GroupMembershipTab;
